import type { CSSProperties, ReactElement } from 'react';

import { cn } from '@/shared/lib/utils';
import { Highlight } from '@/shared/ui/highlight';

import { SLIDE_FRAME, SlideEyebrow, SlideNumber } from './slide-parts';

/**
 * ⚠️ **전부 임시 데이터다. 실제 후기로 교체하기 전에는 공개하지 않는다.**
 *
 * 지어낸 후기는 신뢰 장치가 아니라 리스크다 — 한 번 들키면 랜딩의 다른 사실까지
 * 함께 의심받는다. 실제 수강생에게 받은 문장만 넣는다.
 *
 * 받을 때는 「좋았어요」가 아니라 **무엇이 바뀌었는지**를 묻는다.
 *
 * **확인할 수 없는 말은 쓰지 않는다.** 「지금도 돌아가고 있어요」, 「아직도 매주 돕니다」 같은
 * 문장은 우리가 확인할 방법이 없다. 본문에서 같은 이유로 걷어낸 말을 후기에 남겨 두면
 * 남의 입을 빌려 같은 주장을 하는 셈이 된다. 겪은 사람이 실제로 그렇게 말했더라도,
 * 지어낸 문장과 구별되지 않으면 랜딩의 다른 사실까지 함께 의심받는다.
 */
export type Testimonial = {
  readonly id: string;
  readonly body: string;
  /** 이름 대신 맥락. 개인 식별 정보를 싣지 않는다 */
  readonly who: string;
};

export const TESTIMONIALS: readonly Testimonial[] = [
  {
    id: 't1',
    body: '매주 월요일 오전을 보고서 취합에 썼습니다. 지금은 아침에 초안이 채워져 있어서 확인만 하면 됩니다. 회의가 훨씬 빨라졌습니다.',
    who: '제조업 · 기획팀',
  },
  {
    id: 't2',
    body: '제가 실제로 쓰는 견적서를 가지고 만들었습니다. 교육 다음 날부터 바로 썼고, 따로 정리할 게 없었습니다.',
    who: '유통업 · 영업관리',
  },
  {
    id: 't3',
    body: '그동안 온라인에 노출된 적 없던 동네 가게들이 지도에 올라왔습니다. 학생들이 실제로 다니는 곳이라 정보가 현장과 맞았고, 지역을 모르고 온 신입생들이 바로 쓸 수 있는 자료가 됐습니다.',
    who: '대학 · 산학협력 담당',
  },
  {
    id: 't4',
    body: '설명만 듣고 끝날 줄 알았는데, 마지막 날에 실제로 돌아가는 것이 하나 나왔습니다. 팀에서 바로 쓰기 시작했습니다.',
    who: 'IT·스타트업 · 운영',
  },
  {
    id: 't5',
    body: '팀에 개발 아는 사람이 없었습니다. 막히는 지점마다 왜 그런지를 같이 짚어줘서, 다음엔 혼자 고칠 수 있었습니다.',
    who: '유통업 · 마케팅',
  },
  {
    id: 't6',
    body: '보고 양식을 바꾸려고 시작했는데, 시간을 재보니 작성보다 자료 모으는 데 더 걸렸습니다. 손댈 곳을 정확히 찾은 게 가장 큰 소득이었습니다.',
    who: '공공기관 · 사업팀',
  },
];

function TestimonialCard({ item }: { item: Testimonial }): ReactElement {
  return (
    <li className="flex flex-col gap-4 rounded-3xl bg-background p-6 shadow-[0_16px_32px_-24px_rgb(6_17_11/0.35)]">
      {/* 따옴표를 글자로 넣으면 본문에 섞여 안 보인다. 동그라미에 담아 「인용」임을 먼저 알린다 */}
      <span
        aria-hidden="true"
        className="grid size-8 place-items-center rounded-full bg-primary font-serif text-[1.5rem] leading-none text-on-mint"
      >
        “
      </span>
      <p className="text-[15px] leading-[1.85] tracking-[-0.018em]">{item.body}</p>
      <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
        <span aria-hidden="true" className="h-px w-4 bg-border" />
        {item.who}
      </p>
    </li>
  );
}

/**
 * 세로 띠 한 줄. 같은 목록을 두 벌 이어 붙이고 한 벌 높이만큼 밀어 이음매 없이 반복시킨다.
 * 두 번째 벌은 화면에만 있는 복제본이라 `aria-hidden` 으로 감춘다 —
 * 안 그러면 스크린리더가 같은 후기를 두 번 읽는다.
 */
function MarqueeColumn({
  items,
  reverse = false,
  duration,
  className,
}: {
  items: readonly Testimonial[];
  reverse?: boolean;
  duration: string;
  className?: string;
}): ReactElement {
  const style: CSSProperties = {
    animationName: reverse ? 'marquee-down' : 'marquee-up',
    animationDuration: duration,
  };

  return (
    <div className={cn('flex flex-col gap-4 [--marquee-gap:1rem]', className)}>
      {[false, true].map((isClone) => (
        <ul
          key={isClone ? 'clone' : 'origin'}
          aria-hidden={isClone || undefined}
          style={style}
          className="u-marquee flex shrink-0 flex-col gap-4"
        >
          {items.map((item) => (
            <TestimonialCard key={item.id} item={item} />
          ))}
        </ul>
      ))}
    </div>
  );
}

/** 두 줄에 번갈아 나눈다 — 한 줄에 몰면 같은 흐름이 반복돼 보인다 */
const LEFT_COLUMN = TESTIMONIALS.filter((_, index) => index % 2 === 0);
const RIGHT_COLUMN = TESTIMONIALS.filter((_, index) => index % 2 === 1);

/**
 * 후기 — 슬라이드 한 장. 왼쪽 문장, 오른쪽 민트 패널 안에서 두 줄이 위아래로 흐른다.
 *
 * 세로로 쌓으면 「읽어야 하는 것」이 되고, 흘려보내면 「지나가며 눈에 걸리는 것」이 된다.
 * 후기는 정독하는 물건이 아니라 **여러 개가 있다는 사실 자체가 메시지**다.
 * 두 줄을 반대 방향으로 움직이고, 위아래 가장자리는 마스크로 흐려 잘린 카드가 깨져 보이지 않게 한다.
 * 마우스를 올리면 멈춘다(global.css `.group:hover .u-marquee`). 좁은 화면은 한 줄만 흐른다.
 */
export function EducationTestimonials(): ReactElement {
  return (
    <section aria-labelledby="edu-voice-heading" className={cn(SLIDE_FRAME, 'group')}>
      <SlideNumber>05</SlideNumber>
      <div className="u-container grid items-center gap-12 py-20 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20 lg:py-16">
        <div className="flex flex-col items-start gap-7">
          <SlideEyebrow>Review · 후기</SlideEyebrow>
          <h2
            id="edu-voice-heading"
            className="text-[clamp(2rem,1.3rem+2.4vw,3.25rem)] font-bold leading-[1.22] tracking-[-0.05em]"
          >
            먼저 해본 분들의 <Highlight>이야기</Highlight>
          </h2>
          <p className="max-w-[32rem] text-[16px] leading-[1.85] text-muted-foreground lg:text-[17px]">
            교육이 끝난 뒤 무엇이 달라졌는지 들어 보세요.
          </p>
        </div>

        <div className="h-[min(68svh,38rem)] overflow-hidden rounded-[36px] bg-mint-soft/55 px-4 [mask-image:linear-gradient(to_bottom,transparent,black_7%,black_93%,transparent)] sm:px-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <MarqueeColumn items={LEFT_COLUMN} duration="44s" />
            <MarqueeColumn items={RIGHT_COLUMN} reverse duration="52s" className="hidden sm:flex" />
          </div>
        </div>
      </div>
    </section>
  );
}
